import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Eye, EyeOff, ExternalLink, ImagePlus } from "lucide-react";
import RichTextEditor from "@/components/admin/RichTextEditor";
import MediaPicker from "@/pages/admin/MediaPicker";

const emptyPage = {
  slug: "", title: "", subtitle: "", body: "", hero_image_url: "",
  seo_description: "", is_published: true,
};

export default function AdminPages() {
  const { authedClient } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPage);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/admin/pages");
      setPages(data);
    } catch { toast.error("Could not load pages"); } finally { setLoading(false); }
  }, [authedClient]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      if (editing === "new") {
        const { data } = await authedClient().post("/admin/pages", form);
        setPages([data, ...pages]);
        toast.success("Page created - remember to add it to the Navigation menu!");
      } else {
        const { data } = await authedClient().patch(`/admin/pages/${editing}`, form);
        setPages(pages.map(p => p.id === editing ? data : p));
        toast.success("Saved");
      }
      setEditing(null); setForm(emptyPage);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const togglePublished = async (p) => {
    try {
      const { data } = await authedClient().patch(`/admin/pages/${p.id}`, { is_published: !p.is_published });
      setPages(pages.map(x => x.id === p.id ? data : x));
    } catch { toast.error("Could not toggle"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete page "${p.title}"?`)) return;
    try {
      await authedClient().delete(`/admin/pages/${p.id}`);
      setPages(pages.filter(x => x.id !== p.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-pages-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow mb-2">★ CMS</p>
          <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Custom Pages</h1>
          <p className="text-sm text-[color:var(--ink-soft)] mt-1">
            Create brand-new pages (published at <code>/pages/&lt;slug&gt;</code>). After creating a page,
            head to <strong>Navigation</strong> to link it in the menu.
          </p>
        </div>
        {!editing && (
          <button onClick={() => { setForm(emptyPage); setEditing("new"); }} className="btn-primary" data-testid="admin-pages-new">
            <Plus className="w-4 h-4" /> New page
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={save} className="bg-[color:var(--cream)] rounded-2xl p-6 mb-8 space-y-4" data-testid="admin-pages-form">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-[color:var(--blue-ink)]">{editing === "new" ? "New page" : "Edit page"}</h2>
            <button type="button" onClick={() => { setEditing(null); setForm(emptyPage); }}><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title *"><input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className={ic} data-testid="pages-form-title" /></Field>
            <Field label="Slug (URL)"><input value={form.slug || ""} onChange={e => setForm({...form, slug: e.target.value})} className={ic} placeholder="auto from title" /></Field>
          </div>
          <Field label="Subtitle (shown under the title on the page)">
            <input value={form.subtitle || ""} onChange={e => setForm({...form, subtitle: e.target.value})} className={ic} />
          </Field>
          <Field label="SEO description (Google search summary)">
            <textarea rows={2} value={form.seo_description || ""} onChange={e => setForm({...form, seo_description: e.target.value})} className={ic} />
          </Field>
          <Field label="Hero image (optional)">
            <div className="flex items-center gap-3">
              {form.hero_image_url ? (
                <img src={form.hero_image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.hero_image_url}` : form.hero_image_url}
                     alt="" className="w-32 h-20 object-cover rounded-lg border border-[color:var(--line)]" />
              ) : (
                <div className="w-32 h-20 rounded-lg border border-dashed border-[color:var(--line)] flex items-center justify-center text-[color:var(--muted)]">
                  <ImagePlus className="w-5 h-5" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input value={form.hero_image_url || ""} onChange={e => setForm({...form, hero_image_url: e.target.value})} className={ic} placeholder="/api/files/... or full URL" />
                <div className="flex gap-2">
                  <button type="button" onClick={() => setPickerOpen(true)} className="btn-ghost text-sm py-2 px-3">Pick from library</button>
                  {form.hero_image_url && <button type="button" onClick={() => setForm({...form, hero_image_url: ""})} className="text-sm text-red-600 hover:underline">Remove</button>}
                </div>
              </div>
            </div>
          </Field>
          <Field label="Page body">
            <RichTextEditor value={form.body} onChange={(html) => setForm(f => ({...f, body: html}))} />
          </Field>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_published} onChange={e => setForm({...form, is_published: e.target.checked})} className="accent-[color:var(--blue)]" />
            Published (visible to visitors)
          </label>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" data-testid="pages-form-save"><Save className="w-4 h-4" /> Save</button>
            <button type="button" onClick={() => { setEditing(null); setForm(emptyPage); }} className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}

      {loading ? <p className="text-center text-[color:var(--muted)] py-8">Loading…</p>
        : pages.length === 0 ? (
          <div className="text-center py-12 rounded-2xl border border-dashed border-[color:var(--line)]">
            <p className="text-[color:var(--muted)]">No custom pages yet.</p>
            <p className="text-xs text-[color:var(--muted)] mt-2">Click &ldquo;New page&rdquo; to create one. It will be published at <code>/pages/&lt;slug&gt;</code>.</p>
          </div>
        )
        : (
          <div className="space-y-3">
            {pages.map(p => (
              <div key={p.id} className={`p-4 rounded-xl border ${p.is_published ? "border-[color:var(--line)] bg-white" : "border-dashed border-[color:var(--line)] bg-[color:var(--cream)]/50 opacity-70"}`} data-testid={`admin-page-row-${p.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[color:var(--blue-ink)]">{p.title}</div>
                    <div className="text-xs text-[color:var(--muted)] mt-0.5">
                      <a href={`/pages/${p.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[color:var(--blue-deep)]">
                        /pages/{p.slug} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    {p.subtitle && <div className="text-sm text-[color:var(--ink-soft)] mt-1">{p.subtitle}</div>}
                  </div>
                  <div className="flex items-start gap-1">
                    <button onClick={() => togglePublished(p)} className="p-1.5 rounded hover:bg-[color:var(--cream)]" title={p.is_published ? "Published" : "Draft"}>
                      {p.is_published ? <Eye className="w-4 h-4 text-[color:var(--blue-deep)]" /> : <EyeOff className="w-4 h-4 text-[color:var(--muted)]" />}
                    </button>
                    <button onClick={() => { setForm({...emptyPage, ...p}); setEditing(p.id); }} className="p-1.5 rounded hover:bg-[color:var(--cream)]"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p)} className="p-1.5 rounded text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={(a) => setForm(f => ({...f, hero_image_url: a.public_url}))} />
    </div>
  );
}

const ic = "w-full rounded-lg border border-[color:var(--line)] px-3 py-2 bg-white focus:outline-none focus:border-[color:var(--blue)]";
function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-[color:var(--ink-soft)] mb-1">{label}</label>{children}</div>;
}
